import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCopilot, AskAgentButton } from '../copilot';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ClipboardText,
  ListNumbers,
  Hand,
  Trophy,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Warning,
  Info,
  CaretRight,
  User,
  Users,
  Play,
  Pause,
  Timer,
  ArrowRight,
  Plus,
  SkipForward,
} from '@phosphor-icons/react';

export const DraftBoardPage = () => {
  const { api, user, isAdmin, isSupervisorPlus } = useAuth();
  const { openCopilot, setEntityContext } = useCopilot();
  
  // State
  const [windows, setWindows] = useState([]);
  const [activeWindow, setActiveWindow] = useState(null);
  const [draftState, setDraftState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [pickDialogOpen, setPickDialogOpen] = useState(false);
  const [passDialogOpen, setPassDialogOpen] = useState(false);
  const [createWindowOpen, setCreateWindowOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pickReason, setPickReason] = useState('');
  const [passReason, setPassReason] = useState('');
  
  // New window form
  const [newWindow, setNewWindow] = useState({
    name: '',
    mode: 'DRAFT',
    turn_seconds: 300
  });
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(null);
  const timerRef = useRef(null);

  // Fetch windows
  const fetchWindows = useCallback(async () => {
    try {
      const response = await api.get('/canvass/windows');
      setWindows(response.data || []);
      
      // Find active window
      const active = response.data?.find(w => w.status === 'open');
      if (active) {
        setActiveWindow(active);
        fetchDraftState(active.id);
      } else {
        setActiveWindow(null);
        setDraftState(null);
      }
    } catch (error) {
      console.error('Failed to fetch windows:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchDraftState = useCallback(async (windowId) => {
    try {
      const response = await api.get(`/canvass/windows/${windowId}/draft-state`);
      setDraftState(response.data);
      
      // Set initial timer
      if (response.data?.time_remaining !== null && response.data?.time_remaining !== undefined) {
        setTimeRemaining(Math.floor(response.data.time_remaining));
      }
    } catch (error) {
      console.error('Failed to fetch draft state:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchWindows();
    
    // Poll for updates every 5 seconds
    const pollInterval = setInterval(() => {
      if (activeWindow) {
        fetchDraftState(activeWindow.id);
      }
    }, 5000);
    
    return () => clearInterval(pollInterval);
  }, [fetchWindows, activeWindow]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0 && draftState?.window?.status === 'open') {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timeRemaining, draftState?.window?.status]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCreateWindow = async () => {
    if (!newWindow.name) {
      toast.error('Window name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/canvass/windows', newWindow);
      toast.success('Draft window created');
      setCreateWindowOpen(false);
      setNewWindow({ name: '', mode: 'DRAFT', turn_seconds: 300 });
      fetchWindows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create window');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenWindow = async (windowId) => {
    try {
      await api.post(`/canvass/windows/${windowId}/open`);
      toast.success('Draft window opened');
      fetchWindows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open window');
    }
  };

  const handleCloseWindow = async (windowId) => {
    try {
      await api.post(`/canvass/windows/${windowId}/close`);
      toast.success('Draft window closed');
      fetchWindows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close window');
    }
  };

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    setPickReason('');
    // Auto-select first available role if job has crew requirements
    if (job.has_crew_requirements && job.crew_status?.length > 0) {
      const availableRole = job.crew_status.find(r => !r.is_filled && r.user_eligible);
      setSelectedRole(availableRole?.role_key || '');
    } else {
      setSelectedRole('');
    }
    setPickDialogOpen(true);
  };

  const handlePick = async () => {
    if (!selectedJob || !activeWindow) return;
    
    // Validate role selection for crew jobs
    if (selectedJob.has_crew_requirements && !selectedRole) {
      toast.error('Please select a crew role');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        job_id: selectedJob.id,
        reason: pickReason || undefined
      };
      
      if (selectedJob.has_crew_requirements && selectedRole) {
        payload.crew_role = selectedRole;
      }
      
      const response = await api.post(`/canvass/windows/${activeWindow.id}/pick`, payload);
      
      let successMsg = `Picked: ${response.data.job_title}`;
      if (response.data.crew_role) {
        successMsg += ` as ${response.data.crew_role}`;
      }
      toast.success(successMsg);
      
      setPickDialogOpen(false);
      setSelectedJob(null);
      setSelectedRole('');
      fetchDraftState(activeWindow.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make pick');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePass = async () => {
    if (!activeWindow) return;
    
    setSubmitting(true);
    try {
      await api.post(`/canvass/windows/${activeWindow.id}/pass`, {
        reason: passReason || undefined
      });
      
      toast.success('Turn passed');
      setPassDialogOpen(false);
      setPassReason('');
      fetchDraftState(activeWindow.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pass');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdvanceTurn = async () => {
    if (!activeWindow) return;
    
    const reason = prompt('Reason for advancing turn (required):');
    if (!reason) return;
    
    try {
      await api.post(`/canvass/windows/${activeWindow.id}/advance-turn`, { reason });
      toast.success('Turn advanced');
      fetchDraftState(activeWindow.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to advance turn');
    }
  };

  const isYourTurn = draftState?.your_turn;

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="draft-board-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mb-2">
            Draft Board
          </h1>
          <p className="text-slate-600">
            Turn-based job selection - lowest overtime picks first
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isSupervisorPlus() && (
            <Button variant="outline" onClick={() => setCreateWindowOpen(true)} data-testid="create-window-btn">
              <Plus size={16} className="mr-2" />
              New Draft
            </Button>
          )}
          <AskAgentButton
            module="Quick Canvass"
            prompts={[
              { label: 'Draft rules', prompt: 'How does the turn-based draft work?' },
              { label: 'My turn', prompt: 'When is my turn to pick?' },
            ]}
          />
        </div>
      </div>

      {/* Your Turn Banner */}
      {isYourTurn && draftState?.window?.status === 'open' && (
        <div className="mb-6 p-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl text-white shadow-lg animate-pulse-slow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Hand size={24} weight="fill" />
              </div>
              <div>
                <h2 className="text-xl font-bold">You are on the clock!</h2>
                <p className="text-white/90">Select a job to pick or pass your turn</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-mono font-bold">{formatTime(timeRemaining)}</div>
                <div className="text-sm text-white/80">Time Remaining</div>
              </div>
              <Button 
                variant="secondary" 
                onClick={() => setPassDialogOpen(true)}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <SkipForward size={16} className="mr-2" />
                Pass
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* No Active Window */}
      {!loading && !activeWindow && (
        <Card className="mb-6">
          <CardContent className="p-8 text-center">
            <ClipboardText size={48} className="mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No Active Draft</h3>
            <p className="text-slate-500 mb-4">There is no draft window currently open.</p>
            {isSupervisorPlus() && windows.filter(w => w.status === 'draft').length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Pending draft windows:</p>
                {windows.filter(w => w.status === 'draft').map(w => (
                  <div key={w.id} className="flex items-center justify-center gap-2">
                    <span className="font-medium">{w.name}</span>
                    <Button size="sm" onClick={() => handleOpenWindow(w.id)}>
                      <Play size={14} className="mr-1" />
                      Open
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active Draft Board */}
      {draftState && (
        <>
          {/* Window Info Bar */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Badge variant="default" className="bg-emerald-500">
                    {draftState.window.mode}
                  </Badge>
                  <h3 className="font-semibold text-lg">{draftState.window.name}</h3>
                  <Badge variant="outline">Round {draftState.window.round}</Badge>
                </div>
                <div className="flex items-center gap-4">
                  {draftState.current_turn_user && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-500">Current Picker:</span>
                      <span className="font-medium">
                        {draftState.current_turn_user.name}
                        {draftState.your_turn && <span className="text-emerald-600"> (You!)</span>}
                      </span>
                    </div>
                  )}
                  {isSupervisorPlus() && (
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={handleAdvanceTurn}>
                        <ArrowRight size={14} className="mr-1" />
                        Skip Turn
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleCloseWindow(activeWindow.id)}>
                        <Pause size={14} className="mr-1" />
                        Close
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Panel - Pick Order */}
            <div className="lg:col-span-3">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ListNumbers size={20} />
                    Pick Order
                  </CardTitle>
                  <CardDescription>Lowest overtime picks first</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {draftState?.ranked_order?.map((person, idx) => {
                          const isCurrent = idx === draftState.window.current_pick_index;
                          const hasPicked = draftState.picks?.some(
                            p => p.picked_by_user_id === person.user_id && p.action === 'PICK'
                          );
                          
                          return (
                            <TooltipProvider key={person.user_id}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={`p-3 flex items-center gap-3 transition-all ${
                                      isCurrent 
                                        ? 'bg-emerald-50 border-l-4 border-emerald-500' 
                                        : person.user_id === user?.id 
                                          ? 'bg-blue-50' 
                                          : 'hover:bg-slate-50'
                                    }`}
                                    data-testid={`pick-order-${person.user_id}`}
                                  >
                                    <Badge
                                      variant={isCurrent ? 'default' : 'outline'}
                                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                        isCurrent ? 'bg-emerald-500' : ''
                                      }`}
                                    >
                                      {person.rank}
                                    </Badge>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-medium truncate ${
                                        isCurrent ? 'text-emerald-700' : 'text-slate-900'
                                      }`}>
                                        {person.name}
                                        {person.user_id === user?.id && ' (You)'}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        {person.overtime_hours?.toFixed(1)}h OT
                                      </p>
                                    </div>
                                    {hasPicked && (
                                      <CheckCircle size={16} className="text-emerald-500" weight="fill" />
                                    )}
                                    {isCurrent && (
                                      <Timer size={16} className="text-emerald-600 animate-pulse" />
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>OT: {person.overtime_hours?.toFixed(1)}h</p>
                                  <p>Last OT: {person.last_overtime_date || 'N/A'}</p>
                                  <p className="text-xs text-slate-400 mt-1">
                                    Tie-breaker: Earlier last OT date wins
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Center Panel - Available Jobs */}
            <div className="lg:col-span-5">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardText size={20} />
                    Available Jobs
                  </CardTitle>
                  <CardDescription>
                    {draftState?.available_jobs?.length || 0} jobs remaining
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {draftState?.available_jobs?.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <ClipboardText size={48} className="mx-auto mb-4 opacity-50" />
                      <p>All jobs have been picked</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {draftState?.available_jobs?.map((job) => {
                        const hasOpenSlot = job.has_crew_requirements 
                          ? job.crew_status?.some(r => !r.is_filled && r.user_eligible)
                          : true;
                        const canPick = isYourTurn && job.eligibility?.eligible && hasOpenSlot;
                        
                        return (
                          <div
                            key={job.id}
                            className={`p-4 border rounded-lg transition-all ${
                              canPick 
                                ? 'border-emerald-300 bg-emerald-50/50 hover:shadow-md cursor-pointer' 
                                : 'border-slate-200 opacity-75'
                            }`}
                            onClick={() => canPick && handleSelectJob(job)}
                            data-testid={`job-card-${job.id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-slate-900 mb-1">
                                  {job.title}
                                </h4>
                                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-2">
                                  {job.location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin size={14} />
                                      {job.location}
                                    </span>
                                  )}
                                  {job.start_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar size={14} />
                                      {job.start_date}
                                    </span>
                                  )}
                                  {job.required_classification && (
                                    <Badge variant="outline" className="text-xs">
                                      {job.required_classification}
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Crew Status */}
                                {job.has_crew_requirements && job.crew_status?.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {job.crew_status.map((crew) => (
                                      <div 
                                        key={crew.role_key} 
                                        className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                                          crew.is_filled 
                                            ? 'bg-emerald-100 text-emerald-700' 
                                            : 'bg-slate-100 text-slate-600'
                                        }`}
                                      >
                                        {crew.is_filled ? (
                                          <CheckCircle size={12} weight="fill" />
                                        ) : (
                                          <Clock size={12} />
                                        )}
                                        {crew.role_name}: {crew.filled_count}/{crew.required_count}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                {canPick ? (
                                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                                    Pick
                                  </Button>
                                ) : isYourTurn && !job.eligibility?.eligible ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button size="sm" variant="ghost" disabled>
                                          <XCircle size={14} className="mr-1" />
                                          Ineligible
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs">
                                        {job.eligibility?.reasons?.map((r, i) => (
                                          <p key={i}>{r}</p>
                                        ))}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : isYourTurn && !hasOpenSlot ? (
                                  <Badge variant="secondary" className="text-xs">
                                    No eligible slots
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-xs">
                                    Waiting
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Pick History */}
            <div className="lg:col-span-4">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Trophy size={20} />
                    Pick History
                  </CardTitle>
                  <CardDescription>Who picked what</CardDescription>
                </CardHeader>
                <CardContent>
                  {draftState?.picks?.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Trophy size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No picks yet</p>
                      <p className="text-sm mt-1">Waiting for first selection</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[450px]">
                      <div className="space-y-3 pr-4">
                        {draftState?.picks?.slice().reverse().map((pick) => (
                          <div
                            key={pick.id}
                            className={`p-3 border rounded-lg ${
                              pick.action === 'PICK' 
                                ? 'border-emerald-200 bg-emerald-50/50' 
                                : pick.action === 'PASS'
                                  ? 'border-amber-200 bg-amber-50/50'
                                  : 'border-red-200 bg-red-50/50'
                            }`}
                            data-testid={`pick-history-${pick.id}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  R{pick.round} #{pick.pick_index + 1}
                                </Badge>
                                <span className="font-medium text-sm">
                                  {pick.picked_by_name}
                                </span>
                              </div>
                              <Badge 
                                variant={pick.action === 'PICK' ? 'default' : 'secondary'}
                                className={
                                  pick.action === 'PICK' 
                                    ? 'bg-emerald-500' 
                                    : pick.action === 'TIMEOUT' 
                                      ? 'bg-red-500 text-white' 
                                      : ''
                                }
                              >
                                {pick.action}
                              </Badge>
                            </div>
                            {pick.action === 'PICK' && (
                              <div>
                                <p className="text-sm text-slate-700">{pick.job_title}</p>
                                {pick.role_name && (
                                  <Badge variant="secondary" className="text-xs mt-1">
                                    as {pick.role_name}
                                  </Badge>
                                )}
                              </div>
                            )}
                            {pick.reason && (
                              <p className="text-xs text-slate-500 mt-1">
                                Reason: {pick.reason}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(pick.picked_at).toLocaleTimeString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Pick Confirmation Dialog */}
      <Dialog open={pickDialogOpen} onOpenChange={setPickDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Pick</DialogTitle>
            <DialogDescription>
              {selectedJob?.has_crew_requirements 
                ? 'Select your role and confirm your pick'
                : 'Are you sure you want to pick this job?'}
            </DialogDescription>
          </DialogHeader>

          {selectedJob && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">{selectedJob.title}</h4>
                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  {selectedJob.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {selectedJob.location}
                    </span>
                  )}
                  {selectedJob.start_date && (
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {selectedJob.start_date}
                    </span>
                  )}
                </div>
                
                {/* Crew Status Summary */}
                {selectedJob.has_crew_requirements && selectedJob.crew_status?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs font-medium text-slate-600 mb-2">Crew Status:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedJob.crew_status.map((crew) => (
                        <div 
                          key={crew.role_key} 
                          className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                            crew.is_filled 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {crew.is_filled ? <CheckCircle size={12} weight="fill" /> : <Clock size={12} />}
                          {crew.role_name}: {crew.filled_count}/{crew.required_count}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Role Selection for Crew Jobs */}
              {selectedJob.has_crew_requirements && (
                <div>
                  <Label htmlFor="crew-role">Select Your Role *</Label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose a role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedJob.crew_status?.filter(r => !r.is_filled).map((role) => (
                        <SelectItem 
                          key={role.role_key} 
                          value={role.role_key}
                          disabled={!role.user_eligible}
                        >
                          <div className="flex items-center gap-2">
                            <span>{role.role_name}</span>
                            <span className="text-slate-400">
                              ({role.filled_count}/{role.required_count} filled)
                            </span>
                            {!role.user_eligible && (
                              <Badge variant="secondary" className="text-xs ml-2">
                                Not Eligible
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedJob.crew_status?.find(r => r.role_key === selectedRole && !r.user_eligible) && (
                    <p className="text-xs text-red-500 mt-1">
                      {selectedJob.crew_status.find(r => r.role_key === selectedRole)?.eligibility_reasons?.join(', ')}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="pick-reason">Reason (optional)</Label>
                <Input
                  id="pick-reason"
                  value={pickReason}
                  onChange={(e) => setPickReason(e.target.value)}
                  placeholder="Why you selected this job..."
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPickDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePick} 
              disabled={submitting || (selectedJob?.has_crew_requirements && !selectedRole)}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="confirm-pick-btn"
            >
              {submitting ? 'Picking...' : 'Confirm Pick'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pass Confirmation Dialog */}
      <Dialog open={passDialogOpen} onOpenChange={setPassDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pass Turn</DialogTitle>
            <DialogDescription>
              Are you sure you want to pass your turn?
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-800">
                  If you pass, you will not be able to pick until the next round (if applicable).
                </p>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="pass-reason">Reason (optional)</Label>
            <Input
              id="pass-reason"
              value={passReason}
              onChange={(e) => setPassReason(e.target.value)}
              placeholder="Why you are passing..."
              className="mt-1"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPassDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePass} 
              disabled={submitting}
              variant="secondary"
              data-testid="confirm-pass-btn"
            >
              {submitting ? 'Passing...' : 'Confirm Pass'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Window Dialog */}
      <Dialog open={createWindowOpen} onOpenChange={setCreateWindowOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Draft Window</DialogTitle>
            <DialogDescription>
              Set up a new canvass draft session
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="window-name">Window Name</Label>
              <Input
                id="window-name"
                value={newWindow.name}
                onChange={(e) => setNewWindow({ ...newWindow, name: e.target.value })}
                placeholder="e.g., January 2026 Draft"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="window-mode">Mode</Label>
              <Select
                value={newWindow.mode}
                onValueChange={(val) => setNewWindow({ ...newWindow, mode: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft (Turn-based)</SelectItem>
                  <SelectItem value="OPEN_BID">Open Bid (Legacy)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="turn-seconds">Turn Time (seconds)</Label>
              <Input
                id="turn-seconds"
                type="number"
                value={newWindow.turn_seconds}
                onChange={(e) => setNewWindow({ ...newWindow, turn_seconds: parseInt(e.target.value) || 300 })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Default: 300 seconds (5 minutes)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateWindowOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWindow} disabled={submitting} data-testid="create-window-confirm-btn">
              {submitting ? 'Creating...' : 'Create Draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DraftBoardPage;
